import { FilterStage, StageParameters } from '@data/Simulations/pipeline/Stage';
import { RaycastMeshData } from '@rendering/DesignSchemes/DesignSchemeUtils';
import { SensorData } from 'Simulations/SensorPoint';
import { Box3, Intersection, Raycaster, Vector3 } from 'three';

export interface CloseToDesignSchemesStageParameters extends StageParameters {
    raycastMeshes: RaycastMeshData[];
    globalBox?: Box3;
    maxDistance: number;
}

class CloseToDesignSchemesStage extends FilterStage<CloseToDesignSchemesStageParameters> {
    private worldPoint = new Vector3();
    private localPoint = new Vector3();

    // Raycaster for inside check:
    private raycaster = new Raycaster();
    private readonly insideRayDirection = new Vector3(1, 0, 0);
    private readonly rayIntersections: Intersection[] = [];
    private readonly RAY_EPS = 1e-4;

    protected override filter(sensorData: SensorData): boolean {
        const { raycastMeshes, globalBox, maxDistance } = this.getParams();
        if (!raycastMeshes.length || !globalBox || maxDistance < 0) return false;

        const position = sensorData.getPosition();
        this.worldPoint.set(position.x, position.y, position.z);

        // 1. Check distance from the overall Bounding Box:
        if (globalBox.distanceToPoint(this.worldPoint) > maxDistance) {
            return false;
        }

        // 2. Exclude sensors that are inside the DS (Done only on the sensors close enough --> Not computational
        // expensive as doing on all the sensors):
        if (this.isInsideDesignSchemeGeometry(this.worldPoint, raycastMeshes)) {
            return false;
        }

        // 3. Check distance with every objects inside the global BoundingBox:
        for (const { mesh, box } of raycastMeshes) {
            // 3.1. Check distance from the mesh bounding box:
            if (box.distanceToPoint(this.worldPoint) > maxDistance) continue;

            // 3.2. Check distance from the actual mesh geometry:
            this.localPoint.copy(this.worldPoint);
            mesh.worldToLocal(this.localPoint);

            const geometry: any = mesh.geometry;
            const closest = geometry?.boundsTree?.closestPointToPoint(this.localPoint, undefined, 0, maxDistance);
            if (closest && closest.distance <= maxDistance) {
                return true;
            }
        }

        return false;
    }

    /**
     * Parity Check Algorithm
     *
     * Checks if a point is inside the mesh by casting a ray from the sensor center and counting mesh intersections.
     *
     * @param worldPoint
     * @param raycastMeshes
     *
     * @returns true (inside) or false (outside)
     */
    private isInsideDesignSchemeGeometry(worldPoint: Vector3, raycastMeshes: RaycastMeshData[]): boolean {
        // 1. Cast a horizontal ray from the sensor center and count mesh hits:
        this.raycaster.set(worldPoint, this.insideRayDirection);
        this.raycaster.firstHitOnly = false;

        // 2. Count total intersections with the DS Mesh:
        let totalIntersections = 0;
        for (const { mesh, box } of raycastMeshes) {
            if (!this.raycaster.ray.intersectsBox(box)) continue;

            this.rayIntersections.length = 0;
            mesh.raycast(this.raycaster, this.rayIntersections);
            if (!this.rayIntersections.length) continue;

            totalIntersections += this.countUniqueRayDistances(this.rayIntersections);
        }

        // 3. Odd = inside, Even = outside:
        return totalIntersections % 2 === 1;
    }

    /**
     *
     * Counts only one intersection for triangles that are almost coplanar (i.e. hit at nearly the same distance)
     * to avoid counting multiple hits on the same surface.
     *
     * @param intersections
     *
     * @returns Unique intersection count
     */
    private countUniqueRayDistances(intersections: Intersection[]): number {
        // 1. Sort ints by distance (nearest to furthest) and filter out invalid ones (self hit, < epsilon):
        const sortedDistances = intersections
            .map((hit) => hit.distance)
            .filter((distance) => Number.isFinite(distance) && distance > this.RAY_EPS)
            .sort((a, b) => a - b);

        // 2. Count hit only if it is not almost equal to the previous one:
        let uniqueCount = 0;
        let prevDist = -Infinity;
        for (const currDist of sortedDistances) {
            // 2.1 currDist is not sufficiently farther than prevDist, treat it as same hit and skip.
            if (prevDist + this.RAY_EPS >= currDist) continue;
            uniqueCount++;
            prevDist = currDist;
        }

        return uniqueCount;
    }
}

export default CloseToDesignSchemesStage;
